import React, { useRef, useState, useEffect, useImperativeHandle } from 'react';
import { debounce } from 'lodash';

import './index.module.less';

import { Api } from '@apis/client';
import Http from '@helpers/Http';
import { defaultPoint } from '@helpers/Constants';
import FridayService from '@services/friday';
import { transformToBd09, transformToGcj02 } from '@utils/mapUtil';

import BaseMap, { CustomOverlay, Polygon } from '@components/map/Map/BaseMap';
import * as mapUtils from '@components/map/Map/mapUtils';
import { hideLoading, showLoading } from '@widgets/Loading';
import Toast from '@widgets/Toast';

import SearchList from '../widgets/SearchListModal';
import { ShapeType } from '../constants';
import { RegionData } from '@apis/gloabal';

const maxZoom = 20;
const minZoom = 15;

interface IProps {
  editData?: RegionData;
  centerPoint?: { lng: number; lat: number };
  urlParams: { [key: string]: string };
}

const PolyognDetail = React.forwardRef(
  (props: IProps, ref): JSX.Element => {
    const { editData, centerPoint, urlParams } = props;
    const mapRef = useRef(null);
    const [center, setMapCenter] = useState(centerPoint || defaultPoint);
    const [searchLocations, setSearchLocations] = useState(null);
    const [isSearching, setSearchStatus] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [showTopTips, controlDisplayTips] = useState(true);
    const editRegion = useRef(editData);
    const hasChanged = useRef(false);

    const timer = useRef(null);
    const points = useRef([]);
    const [pointData, setPointDatas] = useState([]);

    const onMapLoad = () => {
      console.log('load map');
    };

    useEffect(() => {
      setTimeout(() => {
        initMapEvent();
      }, 1500);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initMapEvent = () => {
      const instanceMap = mapRef.current;
      if (editData) {
        const tempPoints = editData.points?.length
          ? editData.points.map(item => ({
              lng: item[0],
              lat: item[1],
            }))
          : [];
        setPointDatas(tempPoints);
        setTimeout(() => {
          calcAreaPoints(tempPoints);
        }, 50);
      }
      instanceMap?.setCenter(center);
      instanceMap.disableDragging();
      //这里为什么没用touch事件：在绘制过程中,需要能够拖动地图,touch 事件会在拖动时也触发到，所以不用touch 事件
      instanceMap.addListener('click', touchEventHandle);
      // 触摸移动--开启拖动
      instanceMap.addListener('touchmove', function(e: any) {
        instanceMap.enableDragging();
      });
      // 触摸结束--开启禁止拖动
      instanceMap.addListener('touchend', function(e: any) {
        instanceMap.disableDragging();
      });
    };

    const touchEventHandle = (e: any) => {
      if (editRegion.current) {
        return;
      }
      if (points.current?.length >= 50) {
        return Toast.show({
          type: 'strip',
          title: '最多绘制50个点',
        });
      }
      setPointDatas([]);
      //由于这里会有多边形覆盖的问题，因此每次渲染时 需要清除一下多边形再绘制
      const instanceMap = mapRef.current;
      instanceMap.clearOverlayByType('Polygon');
      hasChanged.current = true;
      const point = mapUtils.getCurrentClickPointInfo(e);
      points.current = points.current.concat(point);
      setPointDatas(points.current);
    };

    //根据输入地点模糊查找位置信息
    const inputValue = (value: string) => {
      if (!value) {
        setSearchLocations(null);
      }
      if (typeof timer.current !== 'undefined') {
        clearTimeout(timer.current);
        timer.current = undefined;
      }
      timer.current = setTimeout(() => {
        if (value !== '') {
          search(value);
        }
      }, 500);
    };

    const search = debounce(async (value?: string) => {
      if (!value) {
        setSearchLocations([]);
        hideLoading();
        return;
      }
      clearTimeout(timer.current);
      if (!navigator.onLine) {
        return Toast.show({
          type: 'strip',
          title: '网络异常，请重试',
        });
      }
      showLoading();
      //搜索超时限制6s
      timer.current = setTimeout(() => {
        hideLoading();
        Toast.show({
          type: 'strip',
          title: '网络异常，请重试',
        });
      }, 6000);
      const instanceMap = mapRef.current;
      const result = await instanceMap.locationSearch(value, center);
      hideLoading();
      clearTimeout(timer.current);
      setSearchLocations(result || []);
    }, 500);

    //清除图形
    const clearOverlays = () => {
      const instanceMap = mapRef.current;
      instanceMap.clearOverlays();
    };

    //获取设备定位信息
    const getPosition = async () => {
      setSelectedLocation(null);
      const { data } = await Http.post(Api.GetCardInfoBySncode, {
        snCode: urlParams.sncode,
        company: urlParams.company || 'lingge',
      });

      if (!data?.isOnLine) {
        Toast.show({
          type: 'strip',
          title: '设备离线，请联网后设置',
        });
      }

      //设备在线时下发实时定位指令
      if (data?.isOnLine) {
        await Http.post(Api.PostPositionV1Realtime, {
          snCode: urlParams.sncode,
        }).catch(e => {});
      }
      //获取定位信息(如果设备有历史定位，则使用历史定位，没有历史定位时使用默认位置为北京市定位
      const { data: result } = await Http.post(Api.GetPositionV1Realtime, {
        snCode: urlParams.sncode,
      });
      let location = defaultPoint;
      if (result) {
        location = transformToBd09({ lng: result.longitude, lat: result.latitude });
      }
      const instanceMap = mapRef.current;
      instanceMap.setCenter(location);
      setMapCenter(location);
    };

    const calcAreaPoints = (ps?: any[]) => {
      const p = ps || pointData;
      if (!p?.length) {
        return;
      }
      const instanceMap = mapRef.current;
      instanceMap?.setViewport({
        points: p,
        margins: [20, 20, 20, 20],
      });
    };

    const retryDraw = () => {
      if (editRegion.current) {
        editRegion.current = null;
      }
      clearOverlays();
      setPointDatas([]);
      points.current = [];
    };

    const onSelectItem = (item: any, isSearch?: boolean) => {
      FridayService.addEvent(
        'security_guard_add_edit_guard_area_regional_location_select_address_click',
      );
      const tempPoint = { lng: item.lng, lat: item.lat };
      setSearchStatus(false);
      const instanceMap = mapRef.current;
      instanceMap.setCenter(tempPoint);
      setMapCenter(tempPoint);
    };
    const saveData = () => {
      if (!pointData?.length || pointData?.length <= 2) {
        return Toast.show({
          type: 'strip',
          title: '当前未形成守护区域，请重新绘制',
        });
      }
      const tempPoints = pointData.map((item: { lng: number; lat: number }) => {
        const point = transformToGcj02({ lng: item.lng, lat: item.lat });
        return [point.lng, point.lat];
      });
      const condition = {
        shape: ShapeType.polygon,
        address: '多边形区域',
        points: tempPoints,
      };
      console.log(condition, 'condition');
      return condition;
    };

    const resetData = () => {
      setPointDatas([]);
      if (editRegion.current) {
        editRegion.current = null;
      }
    };

    useImperativeHandle(ref, () => ({
      save: saveData,
      reset: resetData,
      hasData: () => {
        return !!pointData?.length;
      },
      hasChange: () => !!hasChanged.current,
      getMapCenter: () => center,
    }));

    const renderPolyognArea = () => {
      const commonStyles = {
        enableMassClear: true,
        enableClicking: false,
        fillOpacity: 0.3,
        strokeOpacity: 1,
        strokeWeight: 2,
        strokeStyle: 'solid',
        strokeColor: '#1D70F2',
        fillColor: '#1d70f2',
      };
      if (pointData?.length) {
        return (
          <Polygon enableEditing={false} path={pointData} {...commonStyles} />
        );
      }
      return null;
    };

    const renderMarkers = () => {
      let Markers = null;
      if (pointData?.length) {
        Markers = pointData.map(item => {
          return (
            <CustomOverlay position={item}>
              <div styleName="marker" />
            </CustomOverlay>
          );
        });
      }
      return Markers;
    };

    const mapContainerHeight = document.body.clientHeight * 0.87;

    useEffect(() => {
      // 记录窗口高度，避免 安卓手机 键盘挤小屏幕导致页面变形
      document.body.style.overflow = 'hidden';
      const originalHeight =
        document.documentElement.clientHeight || document.body.clientHeight;
      window.onresize = function() {
        document.body.style.height = originalHeight + 'px';
      };

      return () => {
        window.onresize = null;
        document.body.style.overflow = 'auto';
      };
    }, []);

    return (
      <div
        style={{
          width: document.body.clientWidth,
          height: mapContainerHeight,
        }}
      >
        {showTopTips ? (
          <div className="flex flex-center" styleName="tips-wrapper">
            <div className="flex" styleName="tips">
              <span>点击地图绘制多边形区域</span>
              <i
                className="iconfont icon-jiahao"
                style={{ transform: 'rotate(-45deg)' }}
                onClick={() => controlDisplayTips(false)}
              />
            </div>
          </div>
        ) : null}
        <BaseMap
          ref={mapRef}
          onMapLoad={onMapLoad}
          maxZoom={maxZoom}
          minZoom={minZoom}
          zoom={18}
          enableDoubleClickZoom={false}
          center={center}
        >
          {pointData?.length ? renderMarkers() : null}
          {pointData?.length ? renderPolyognArea() : null}
        </BaseMap>
        <div
          className="flex flex-center"
          styleName="icon-genlocation"
          style={{
            position: 'absolute',
            top: mapContainerHeight * 0.9,
            right: 16,
          }}
          onClick={getPosition}
        >
          <i className="iconfont icon-dingwei" />
        </div>
        <div
          className="flex flex-center"
          styleName="icon-reset"
          onClick={pointData?.length ? retryDraw : () => {}}
          style={{
            position: 'absolute',
            top: mapContainerHeight * 0.9,
            left: 16,
            ...(pointData?.length ? {} : { color: '#7F838A' }),
          }}
        >
          重画
        </div>
        <div className="flex" styleName="modal" style={{ height: '14%' }}>
          <div
            className="flex"
            styleName="search-btn-wrapper"
            onClick={() => {
              setSearchStatus(true);
              FridayService.addEvent(
                'security_guard_add_edit_guard_area_regional_location_search_click',
              );
            }}
          >
            <i className="iconfont icon-sousuo" styleName="input-icon" />
            <div styleName="search-btn">搜索地点名称</div>
          </div>
        </div>
        {isSearching ? (
          <SearchList
            items={searchLocations}
            onSelectItem={onSelectItem}
            selectedLocation={selectedLocation}
            onCancel={() => setSearchStatus(false)}
            onSearch={inputValue}
          />
        ) : null}
      </div>
    );
  },
);

export default PolyognDetail;
